import { defineComponent } from "../lib/component.ts";
import { lockScroll, unlockScroll } from "../lib/scroll-lock.ts";

interface ImageLightboxRefs {
  grid?: HTMLElement;

  items?: HTMLButtonElement[];

  dialog?: HTMLDialogElement;

  frame?: HTMLElement;

  image?: HTMLImageElement;

  previous?: HTMLButtonElement;

  next?: HTMLButtonElement;

  close?: HTMLButtonElement;

  status?: HTMLElement;
}

const SCROLL_LOCK_ID = "image-lightbox";

/**
 * Full-screen image viewer for the product gallery.
 *
 * Every `ref="items[]"` button carries the full-size source in
 * `data-full-src` / `data-full-srcset` and its alt text in `data-alt`.
 * Clicking one opens the native `<dialog>` (which handles focus trapping and
 * Escape) on that image; previous / next wrap around the list, clicking the
 * open image advances to the next one, clicking beside it closes.
 *
 * @example
 * <image-lightbox>
 *   <button ref="items[]" data-full-src="…" data-alt="…"><img …></button>
 *   <dialog ref="dialog">
 *     <img ref="image" alt="">
 *     <button ref="previous">Previous</button>
 *     <button ref="next">Next</button>
 *     <button ref="close">Close</button>
 *   </dialog>
 * </image-lightbox>
 */
class ImageLightbox extends defineComponent<ImageLightboxRefs>()(["dialog", "image"]) {
  #index = 0;

  connectedCallback(): void {
    super.connectedCallback();

    const { dialog, previous, next, close } = this.refs;
    const items = this.refs.items ?? [];

    items.forEach((item, index) => {
      item.addEventListener("click", () => this.open(index), { signal: this.signal });
    });

    previous?.addEventListener("click", () => this.show(this.#index - 1), {
      signal: this.signal,
    });
    next?.addEventListener("click", () => this.show(this.#index + 1), { signal: this.signal });
    close?.addEventListener("click", () => this.close(), { signal: this.signal });

    dialog.addEventListener("click", this.#handleBackdropClick, { signal: this.signal });
    this.refs.image.addEventListener("click", () => this.show(this.#index + 1), {
      signal: this.signal,
    });
    dialog.addEventListener("keydown", this.#handleKeydown, { signal: this.signal });
    dialog.addEventListener("close", this.#handleClosed, { signal: this.signal });

    if (items.length < 2) {
      if (previous) previous.hidden = true;
      if (next) next.hidden = true;
    }
  }

  disconnectedCallback(): void {
    if (this.isOpen) unlockScroll(SCROLL_LOCK_ID);
    super.disconnectedCallback();
  }

  get isOpen(): boolean {
    return this.refs.dialog.open;
  }

  get count(): number {
    return this.refs.items?.length ?? 0;
  }

  get index(): number {
    return this.#index;
  }

  open(index: number): void {
    if (this.count === 0) return;
    this.show(index);
    if (this.isOpen) return;
    lockScroll(SCROLL_LOCK_ID);
    this.refs.dialog.showModal();
    this.refs.close?.focus({ preventScroll: true });
  }

  close(): void {
    if (!this.isOpen) return;
    this.refs.dialog.close();
  }

  /** Display the image at `index`, wrapping around both ends. */
  show(index: number): void {
    const count = this.count;
    if (count === 0) return;
    this.#index = ((index % count) + count) % count;

    const item = this.refs.items?.[this.#index];
    if (!item) return;

    const { image, status } = this.refs;
    image.src = item.dataset.fullSrc ?? "";
    if (item.dataset.fullSrcset) {
      image.srcset = item.dataset.fullSrcset;
      image.sizes = "100vh";
    } else {
      image.removeAttribute("srcset");
      image.removeAttribute("sizes");
    }
    image.alt = item.dataset.alt ?? "";
    if (item.dataset.width && item.dataset.height) {
      image.width = Number(item.dataset.width);
      image.height = Number(item.dataset.height);
    }

    if (status) status.textContent = `${this.#index + 1} / ${count}`;
  }

  #handleClosed = (): void => {
    unlockScroll(SCROLL_LOCK_ID);
    this.refs.items?.[this.#index]?.focus({ preventScroll: true });
  };

  #handleBackdropClick = (event: MouseEvent): void => {
    if (event.target === this.refs.dialog || event.target === this.refs.frame) {
      this.close();
    }
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      this.show(this.#index - 1);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      this.show(this.#index + 1);
    }
  };
}

if (!customElements.get("image-lightbox")) {
  customElements.define("image-lightbox", ImageLightbox);
}
